import React, { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { connect } from "react-redux";
import {
    DatePicker,
    Modal,
    TextInput,
    NumberInput,
    Theme,
    HorizonalTile,
    MonthPicker,
    BubbleSelectBlock,
    Text,
    SearchBar,
    Switch,
    Button,
    Bubble
} from "@simplybenefits/sb-ui-library";
import { helpers } from '@simplybenefits/sb-utils';
import { generateReport } from '../../actions/reports.js';
import Report from './Report.js';
import permissionLevel from '../../services/permissions.js';

class ReportOverview extends Component {
    state = {
        modalOpen: false,
        isSentViaEmail: null,
        isSentViaEmailVerbiage: null,
        reportType: null,
        report: null,
        hideReportTable: false,
        isReportTableHidden: false,
        divisionOptions: [],
        experiencePeriodOptions: {},
        classOptions: [],
        advisorOptions: [],
        filters: {},
        requiredFields: null,
        titleExpansionKeys: {},
        reportMeta: {},
        showSaveButton: true,
        initialFetchResults: {},
        isDatePickerOpen: false
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Reports', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    _setIsDatePickerOpen = async (toggleValue) => {
        if (toggleValue === null) {
            await this.setState({
                isDatePickerOpen: false
            });
        }

        if (toggleValue !== null) {
            await this.setState({
                isDatePickerOpen: true
            });
        }
    };

    _filterValueIsSet(field) {
        return typeof this.state.filters[field]?.value === 'string' && this.state.filters[field]?.value?.length > 0;
    }

    // eslint-disable-next-line consistent-return
    _requiredFieldsCheck() {
        if (this.state.reportType === "Demographic Adjustment") {
            return null;
        }

        if (Array.isArray(this.state.requiredFields)) {
            const showSaveButton = this.state.requiredFields.every((field) => this._filterValueIsSet(field));
            this.setState({ showSaveButton });
        }
    }

    _setFilter = async (type, value) => {
        await this.setState({
            filters: {
                ...this.state.filters,
                [type]: {
                    ...this.state.filters[type],
                    value
                }
            }
        });

        this._requiredFieldsCheck();
    };

    _onChangeWithFollowUpRequests = async (type, value) => {
        const followUpRequests = this.state.filters[type]?.followUpRequests;

        if (Array.isArray(followUpRequests)) {
            const followUpResults = await Promise.all(followUpRequests.map(async (e) => {
                // Resetting followup filter.
                if ([ e.resetFilterName, e.resetFilterValue ].every((val) => typeof val === 'string')) {
                    await this._setFilter(e.resetFilterName, e.resetFilterValue);
                }

                // Fetch follow-up data based on value.
                const result = await this.props.dispatch(e.fn(value));
                return [ e.populates, result ];
            }));

            this.setState({ ...Object.fromEntries(followUpResults) });
        }

        await this._setFilter(type, value);
    };

    debouncer = debounce(async (type, value) => {
        const debounceArr = this.state.filters[type]?.debounceArr;

        if (Array.isArray(debounceArr)) {
            const data = await Promise.all(debounceArr.map(async ({ fn, populates }) => {
                const result = await this.props.dispatch(fn(value));
                return [ populates, result ];
            }));

            this.setState({ ...Object.fromEntries(data) });
        }
    }, 500);

    _handleChangeWithDebounce = (type, value) => {
        if (this.state.filters[type]?.debounceArr) {
            this.debouncer(type, value);
        }

        this._setFilter(type, value);
    };

    _setInitialFetchResult = (key, value) => {
        this.setState({
            initialFetchResults: {
                ...this.state.initialFetchResults,
                [key]: {
                    ...this.state.initialFetchResults[key],
                    value
                }
            }
        });
    };

    _handleBubbleListSelector = (filterKey, option) => {
        const selection = [ ...this.state.filters[filterKey].value ];
        const index = selection.findIndex((e) => e === option);

        if (index === -1) {
            selection.push(option);
        } else {
            selection.splice(index, 1);
        }

        this._setFilter(filterKey, selection);
    };

    _handleIsAsyncResult = async (key, fn) => {
        if (this.state.initialFetchResults[key]?.value?.length === 0) {
            this.setState({
                initialFetchResults: {
                    ...this.state.initialFetchResults,
                    [key]: {
                        ...this.state.initialFetchResults[key],
                        error: `${ this.state.initialFetchResults[key].label } is Required`
                    }
                }
            });

            return;
        }

        const result = await this.props.dispatch(fn(this.state.initialFetchResults[key].value));

        if (result === null || !Object.keys(result).length) {
            return;
        }

        if (this.state.reportType === "Demographic Adjustment" && Object.keys(result).length) {
            this.setState({
                showSaveButton: true,
                experiencePeriodOptions: result
            });

            return;
        }

        // eslint-disable-next-line no-unused-vars
        const { error, ...fetchResults } = this.state.initialFetchResults[key];

        this.setState({
            showSaveButton: true,
            initialFetchResults: {
                ...this.state.initialFetchResults,
                [key]: {
                    ...fetchResults,
                    result
                }
            }
        });
    };

    _setBubbleSelect = (type, options) => {
        let newValue = options[0];

        if (this.state.filters[type].value === newValue) {
            newValue = options[1];
        }

        this.setState({
            filters: {
                ...this.state.filters,
                [type]: {
                    ...this.state.filters[type],
                    value: newValue
                }
            }
        });
    };

    /**
     * Used in the datepicker to set local state
     * @param startDate
     * @param endDate
     */
    _submitDate = (dateRange) => {
        this.setState({
            filters: {
                ...this.state.filters,
                dateRange: {
                    ...this.state.filters.dateRange,
                    ...dateRange
                }
            }
        });
    };

    _toggleModal = (modalAction = 'close', reportType = false, filters = {}, initialFetchResults = {}, showSaveButton = true, hideReportTable = false, titleExpansionKeys = {}, requiredFields = null) => {
        if ([ 'Commissions', 'Block', 'Renewal Dates' ].includes(reportType) && this.props.brokerages) {
            const brokerageNames = this.props.brokerages.map((brokerage) => brokerage.name);

            if (Array.isArray(filters.brokerage?.options)) {
                filters.brokerage.options = [ 'All', ...brokerageNames ];
            }
        }

        const divisionOptions = this.props.currentAccount ?
            [ 'All Divisions', ...helpers.sortObjByTitle(this.props.currentAccount.divisions).map((key) => this.props.currentAccount.divisions[key].title) ]
            :
            [];
        const classOptions = this.props.currentAccount ?
            [ 'All Classes', ...helpers.sortObjByTitle(this.props.currentAccount.classes).map((key) => this.props.currentAccount.classes[key].title) ]
            :
            [];

        if (modalAction === 'open') {
            this.setState({
                divisionOptions,
                classOptions,
                advisorOptions: [],
                reportType,
                filters,
                titleExpansionKeys,
                showSaveButton,
                initialFetchResults,
                hideReportTable,
                requiredFields,
                modalOpen: true,
                isSentViaEmail: null,
                isSentViaEmailVerbiage: null
            });
        } else {
            this.setState({
                divisionOptions: [],
                experiencePeriodOptions: {},
                classOptions: [],
                requiredFields: null,
                advisorOptions: [],
                filters,
                isSentViaEmail: null,
                isSentViaEmailVerbiage: null,
                modalOpen: false
            });
        }
    };

    _generateReport = async () => {
        const filterValues = {};
        let isValid = true;

        if (this.state.filters?.dateRange?.error) {
            return null;
        }

        Object.keys(this.state.filters).map((filter) => {
            if (this.state.filters[filter].dependsOn) {
                const { dependsOn } = this.state.filters[filter];
                const { result } = this.state.initialFetchResults[dependsOn];

                if (!result) {
                    isValid = false;
                } else {
                    filterValues[filter] = result[this.state.filters[filter].value];
                }
            } else if (filter === 'advisor') {
                const advisorID = this.state.advisorOptions[this.state.filters[filter]?.value];

                if (advisorID) {
                    filterValues.advisorID = advisorID;
                }
            } else if (filter === 'dateRange') {
                filterValues[filter] = {
                    startDate: this.state.filters[filter].startDate,
                    endDate: this.state.filters[filter].endDate
                };
            } else if (filter === 'brokerage') {
                filterValues.brokerageID = this.state.filters.brokerage.value !== 'All' ?
                    this.props.brokerages.find((brok) => brok.name === this.state.filters.brokerage.value).brokerageID
                    : 'All';
            } else if (filter === 'renewalID') {
                const { getValue, value } = this.state.filters.renewalID;
                filterValues.renewalID = getValue(this.state, value);
                filterValues.groupNum = this.state.initialFetchResults.groupNum.value;
            } else {
                if (this.state.filters[filter].isRequired
                    && !this.state.filters[filter].value
                ) {
                    isValid = false;

                    this.setState({
                        filters: {
                            ...this.state.filters,
                            [filter]: {
                                ...this.state.filters[filter],
                                error: `${ this.state.filters[filter].label } is Required`
                            }
                        }
                    });
                }

                filterValues[filter] = this.state.filters[filter].value;
            }

            return true;
        });

        if (!isValid) {
            return false;
        }

        this._toggleModal('close');

        const accountID = this.props.currentAccount?.meta?.accountID || null;

        // Populate the advisorID field within the filterValues object when the application is in an advisor-specific view.
        if (typeof this.props.currentAdvisor?.advisorID === 'string') {
            filterValues.advisorID = this.props.currentAdvisor.advisorID;
        }

        const report = await this.props.dispatch(generateReport(this.state.reportType, filterValues, accountID));

        if (report) {
            const groupNum = this.props.currentAccount?.meta?.groupNum || null;
            const company = this.props.currentAccount?.meta?.company || null;

            this.setState({
                isSentViaEmail: report.isSentViaEmail || null,
                isSentViaEmailVerbiage: report.verbiage || null,
                report,
                isReportTableHidden: this.state.hideReportTable,
                titleExpansionKeys: this.state.titleExpansionKeys,
                reportMeta: { accountID, groupNum, company, ...filterValues }
            });
        }

        return true;
    };

    _handleAMonthChange = (value) => {
        this.setState({
            filters: {
                ...this.state.filters,
                month: {
                    ...this.state.month,
                    value
                }
            }
        });
    };

    _closeReportDownloadModal = () => {
        this.setState({
            isSentViaEmail: null,
            isSentViaEmailVerbiage: null
        });
    };

    _handleSearchChange = (value) => {
        this.setState({
            query: value
        });
    };

    render() {
        const { reportSectionData, currentAccount } = this.props;
        const { report, modalOpen, filters, initialFetchResults, divisionOptions, experiencePeriodOptions, classOptions, advisorOptions, isReportTableHidden, titleExpansionKeys, reportMeta, isSentViaEmail, isSentViaEmailVerbiage } = this.state;
        const styles = {
            modal: {
                width: 500,
                height: this.state.isDatePickerOpen ? "575px" : "auto",
                flex: "none"
            },
            reportTiles: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                maxWidth: '1000px',
                margin: '0 auto 24px'
            },
            bubbleListSelector: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'baseline'
            }
        };

        let hasSpendingAccounts = false;
        let hasHSA = false;
        let hasLSA = false;

        if (currentAccount) {
            // Find out if the account has HSA or LSA
            if (currentAccount.plan?.data?.spendingAccounts) {
                currentAccount.plan.data.spendingAccounts.map((classSa) => {
                    if (classSa?.coverage && classSa.coverage.isSelected) {
                        if (classSa.coverage.pooledAmountAvailable && classSa.coverage.isPooled) {
                            hasSpendingAccounts = true;
                        }

                        if (classSa.coverage.hsaAmount && classSa.coverage.includeHsa) {
                            hasHSA = true;
                        }

                        if (classSa.coverage.lsaAmount && classSa.coverage.includeLsa) {
                            hasLSA = true;
                        }
                    }

                    return false;
                });
            }
        }

        // Get all the sections that will be shown so that we can let the user know if we can't find any reports from their search
        const allReportSections = Object.keys(reportSectionData).map((subSectionKey) => {
            const reports = reportSectionData[subSectionKey];
            // Get all reports that should be shown to see if we should even show this section
            const reportsToShow = Object.keys(reports).map((reportKey) => {
                const reportData = reports[reportKey];

                // user does not have permission to access the report
                if (reportData.permissionLevels && reportData.permissionLevels.indexOf(this.props.accessType.toUpperCase()) === -1) {
                    return null;
                }

                if (this.state.query
                    && !reportKey.toLowerCase().replace(/\s/g, '').replace('report', '').includes(this.state.query.toLowerCase().replace(/\s/g, ''))
                    && (
                        !reportData.title
                        || !reportData.title.toLowerCase().replace(/\s/g, '').replace('report', '').includes(this.state.query.toLowerCase().replace(/\s/g, ''))
                    )
                    && (
                        !reportData.description
                        || !reportData.description.toLowerCase().replace(/\s/g, '').replace('report', '').includes(this.state.query.toLowerCase().replace(/\s/g, ''))
                    )
                ) {
                    return null;
                }

                // Filter out reports based on account and plan info
                if (reportKey === 'HSA Balances' && !hasSpendingAccounts && !hasHSA) {
                    return null;
                }

                if (reportKey === 'LSA Balances' && !hasSpendingAccounts && !hasLSA) {
                    return null;
                }

                if (reportKey === 'ASO Monthly Summary'
                    && (!currentAccount?.meta?.isAsoHealth && !currentAccount?.meta?.isAsoDental)
                ) {
                    return null;
                }

                return (
                    <HorizonalTile
                        key={reportKey}
                        title={ `${ reportData.title || reportKey } Report` }
                        description={ reportData.description }
                        icon={{
                            name: reportData.icon,
                            color: Theme.colors.skyBlue
                        }}
                        customStyle={{ width: '49%', marginBottom: 16 }}
                        onClick={ () => this._toggleModal('open', reportKey, reportData.filters, reportData.initialFetchResults, reportData.showSaveButton, reportData.hideReportTable, reportData.titleExpansionKeys, reportData.requiredFields) }
                    />
                );
            }).filter((tile) => tile !== null); // filter out tiles that won't be shown

            if (reportsToShow.length === 0) {
                return null;
            }

            return (
                <div key={subSectionKey}>
                    {
                        subSectionKey !== 'All' &&
                            <Text
                                margin="0 0 16px"
                                medium
                                bold
                            >
                                { subSectionKey }
                            </Text>
                    }

                    <div style={ styles.reportTiles }>
                        { reportsToShow }
                    </div>
                </div>
            );
        }).filter((tile) => tile !== null); // filter out sections that won't be shown;

        return (
            <div>
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <SearchBar
                        onChange={ (e) => this._handleSearchChange(e.target.value) }
                        search={ (value) => this._handleSearchChange(value) }
                        placeholder="Search"
                        iconColor={ Theme.colors.skyBlue }
                        customStyle={{
                            margin: '16px auto 32px',
                            width: '100%',
                            backgroundColor: Theme.colors.surface
                        }}
                    />

                    {
                        allReportSections.length === 0 ?
                            <Text
                                margin="0 0 16px"
                                medium
                                bold
                            >
                                No Results Found
                            </Text>
                            :
                            allReportSections
                    }
                </div>

                {
                    report &&
                        <Report
                            report={ report }
                            isReportTableHidden={ isReportTableHidden }
                            titleExpansionKeys={ titleExpansionKeys }
                            reportMeta={ reportMeta }
                        />
                }

                {
                    isSentViaEmail
                        ? <Modal
                            open={ true }
                            onClose={ this._closeReportDownloadModal }
                            title="Report Download"
                        >
                            <Text
                                medium
                                margin="10px 0 5px 0"
                            >
                                { isSentViaEmailVerbiage }
                            </Text>
                        </Modal>
                        : null
                }

                <Modal
                    open={ modalOpen }
                    onClose={ this._toggleModal }
                    small
                    title={ `${ this.state.reportType } Report` }
                    primaryButton={ this.state.showSaveButton ? 
                        {
                            label: "Generate",
                            onClick: this._generateReport
                        }
                        : null
                    }
                >
                    {
                        Object.entries(initialFetchResults).map(([ fetchKey, fetchResult ]) => {
                            // this report is slightly different than others in this component so we have to perform this check in a new way
                            if (this.state.reportType === "Demographic Adjustment" && Object.keys(this.state.experiencePeriodOptions).length) {
                                return null;
                            }

                            return (
                                <div>
                                    <NumberInput
                                        key={ fetchKey }
                                        label={ fetchResult.label }
                                        variant="outlined"
                                        value={ fetchResult.value }
                                        error={ Boolean(this.state.initialFetchResults[fetchKey].error) }
                                        helperText={ 
                                            this.state.reportType === "Demographic Adjustment" ? 
                                                "" 
                                                : 
                                                fetchResult.isRequired ? 
                                                    this.state.initialFetchResults[fetchKey].error
                                                    : 'Leave Blank For All' 
                                        }
                                        onChange={ (e) => this._setInitialFetchResult(fetchKey, e.target.value) }
                                        fullWidth
                                        required={ fetchResult.isRequired }
                                    />
                                    {
                                        (!this.state.initialFetchResults[fetchKey].result) &&
                                    <Button
                                        onClick={() => this._handleIsAsyncResult(fetchKey, fetchResult.fn)}
                                        color="primary"
                                        fullWidth
                                        marginTop={2.5}
                                    >
                                        { fetchResult.buttonText }
                                    </Button>
                                    }
                                </div>
                            );
                        })
                    }

                    {
                        Object.keys(filters).map((filterKey) => {
                            const filter = this.state.filters[filterKey];

                            if (filter.type === 'select') {
                                let options = filter.options;
                                let defaultValue = filters[filterKey].defaultValue;
                                let value = filters[filterKey].value === null && defaultValue ? defaultValue : filters[filterKey].value;
                                let showField = true;

                                switch (filterKey) {
                                    case 'renewalID':
                                        if (Object.keys(experiencePeriodOptions).length === 0) {
                                            showField = false;
                                        }

                                        options = Object.keys(experiencePeriodOptions);
                                        break;
                                    case 'division':
                                        if (filter.populatedBy && Array.isArray(divisionOptions) && divisionOptions.length === 0) {
                                            showField = false;
                                        }

                                        options = divisionOptions;
                                        break;
                                    case 'divisionName':
                                        options = divisionOptions;
                                        break;
                                    case 'class':
                                        if (filter.populatedBy && Array.isArray(classOptions) && classOptions.length === 0) {
                                            showField = false;
                                        }

                                        options = classOptions;
                                        break;
                                    case 'className':
                                        options = classOptions;
                                        break;
                                    case 'patient':
                                        if (!('result' in this.state.initialFetchResults[filter.dependsOn])) {
                                            showField = false;
                                            break;
                                        }

                                        options = Object.keys(this.state.initialFetchResults[filter.dependsOn].result);
                                        break;
                                    case 'advisor':
                                        if (filter.populatedBy && Array.isArray(advisorOptions) && advisorOptions.length === 0) {
                                            showField = false;
                                        }

                                        options = Object.keys(advisorOptions);
                                        break;
                                    default:
                                        options = filter.options;
                                        break;
                                }

                                return (
                                    (showField) &&
                                        <TextInput
                                            key={ filterKey }
                                            label={ filter.label }
                                            variant="outlined"
                                            value={ value || 'All' }
                                            onChange={ (e) => this._onChangeWithFollowUpRequests(filterKey, e.target.value) }
                                            select
                                            selectOptions={ options }
                                            fullWidth
                                        />
                                );
                            } else if (filter.type === 'number') {
                                return (
                                    <NumberInput
                                        key={ filterKey }
                                        label={ filter.label }
                                        variant="outlined"
                                        value={ filters[filterKey].value }
                                        onChange={ (e) => this._handleChangeWithDebounce(filterKey, e.target.value) }
                                        error={ Boolean(filters[filterKey].error) }
                                        helperText={ filter.isRequired ? filters[filterKey].error : 'Leave Blank For All' }
                                        fullWidth
                                        required={ filter.isRequired }
                                    />
                                );
                            } else if (filter.type === 'text') {
                                return (
                                    <TextInput
                                        key={ filterKey }
                                        label={ filter.label }
                                        variant="outlined"
                                        value={ this.state.filters[filterKey].value }
                                        onChange={ (e) => this._setFilter(filterKey, e.target.value) }
                                        error={ Boolean(filters[filterKey].error) }
                                        helperText={ filters[filterKey].error }
                                        placeholder={
                                            [ 'divisionName', 'className' ].includes(filterKey) ?
                                                'This will be ignored if there is no group number'
                                                :
                                                null
                                        }
                                        fullWidth
                                    />
                                );
                            } else if (filter.type === 'date') {
                                return (
                                    <DatePicker
                                        key={ filterKey }
                                        startDate={ filters[filterKey].startDate }
                                        endDate={ filters[filterKey].endDate }
                                        submitDate={ this._submitDate }
                                        defaultSelectedValue={ this.state.reportType === 'Over Age Member Listing Report' ? 'Next 6 months' : filters[filterKey].defaultSelectedValue || "This Year" }
                                        futureDate={ this.state.reportType === 'Over Age Member Listing Report' }
                                        customOptions={ filters[filterKey].customOptions || null }
                                        customStyle={{ marginTop: "16px" }}
                                        isPickerOpen={this._setIsDatePickerOpen}
                                    />
                                );
                            } else if (filter.type === 'bubble-list-selector') {
                                return (
                                    <React.Fragment key={ filterKey } >
                                        <Text medium margin="0">{ filter.label }</Text>
                                        <div style={ styles.bubbleListSelector }>
                                            {
                                                filter.options.map((option, index) => (
                                                    <Bubble
                                                        key={ index }
                                                        label={ option }
                                                        isSelected={ this.state.filters[filterKey].value.includes(option) }
                                                        onClick={() => this._handleBubbleListSelector(filterKey, option)}
                                                    ></Bubble>
                                                ))
                                            }
                                        </div>
                                    </React.Fragment>
                                );
                            } else if (filter.type === 'bubble-select') {
                                return (
                                    <React.Fragment key={ filterKey } >
                                        <Text medium margin="0">{ filter.label }</Text>
                                        <BubbleSelectBlock
                                            key={ filterKey }
                                            first={{
                                                title: filter.options[0],
                                                selected: filters[filterKey].value === filter.options[0],
                                                field: filterKey
                                            }}
                                            second={{
                                                title: filter.options[1],
                                                selected: filters[filterKey].value === filter.options[1],
                                                field: filterKey
                                            }}
                                            handleChange={ () => this._setBubbleSelect(filterKey, filter.options) }
                                        />
                                    </React.Fragment>
                                );
                            } else if (filter.type === 'month') {
                                if ([ 'from', 'to' ].includes(filterKey)) {
                                    return (
                                        <React.Fragment key={filterKey}>
                                            <Text margin="12px 0 0">{ `${ filterKey.charAt(0).toUpperCase() }${ filterKey.slice(1) }` }</Text>
                                            <MonthPicker
                                                value={ filter.value }
                                                onChange={ (value) => this._setFilter(filterKey, value) }
                                                customStyle={{ marginTop: "16px" }}
                                            />
                                        </React.Fragment>
                                    );
                                }

                                return (
                                    <MonthPicker
                                        key={filterKey}
                                        value={ filter.value }
                                        onChange={ (value) => this._setFilter(filterKey, value) }
                                        customStyle={{ marginTop: "16px" }}
                                    />
                                      
                                );
                            } else if (filter.type === 'switch') {
                                return (
                                    <div key={filterKey} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                                        <Text medium bold margin="0">{ filter.label }</Text>
                                        <Switch
                                            checked={ filter.value }
                                            onChange={ () => this._setFilter(filterKey, !filter.value) }
                                        />
                                    </div>
                                );
                            }

                            return '';
                        })
                    }
                </Modal>
            </div>
        );
    }
}

ReportOverview.propTypes = {
    accounts: PropTypes.array,
    cursor: PropTypes.number,
    currentAccount: PropTypes.object,
    currentAdvisor: PropTypes.object
};

/**
 * @param state
 * @returns {{loading: *}}
 */
const mapStateToProps = (state) => ({
    brokerages: state.master.brokerages,
    accessType: state.master.access
});

export default (connect(mapStateToProps)(ReportOverview));